import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  Plus, 
  Filter, 
  Heart, 
  ChevronDown,
  Calendar,
  User,
  Share2,
  X,
  MessageSquare,
  Edit,
  Trash2,
  MoreVertical
} from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import DashboardHeader from '../components/DashboardHeader';
import NavigationCarousel from '../components/NavigationCarousel';
import SearchBar from '../components/SearchBar';
import ProjectUploadModal from '../components/ProjectUploadModal';

const Pinup = () => {
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(true);
  const [activeFilter, setActiveFilter] = useState('all');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProject, setSelectedProject] = useState(null);
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [projects, setProjects] = useState([]);
  const [likedProjects, setLikedProjects] = useState(() => {
    // Load liked projects from localStorage on mount
    const saved = localStorage.getItem('likedProjects');
    return saved ? new Set(JSON.parse(saved)) : new Set();
  });
  const [editingProject, setEditingProject] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const filterRef = useRef(null);

  // Mock data for projects - Real architectural projects
  const initialProjects = [
    {
      id: 1,
      title: 'Barcelona Pavilion Redesign',
      category: 'Cultural',
      author: 'Ahmed Al Mansouri',
      date: '2024-01-15',
      likes: 24,
      image: '/assets/INTERNATIONAL PROJECTS.png',
      description: 'Contemporary reinterpretation of Mies van der Rohe\'s Barcelona Pavilion using sustainable materials and modern construction techniques.',
      tags: ['Modern', 'Sustainable', 'Cultural'],
      status: 'Published'
    },
    {
      id: 2,
      title: 'Vertical Forest Tower',
      category: 'Residential',
      author: 'Sarah Chen',
      date: '2024-01-12',
      likes: 18,
      image: '/assets/Green Architecture.jpeg',
      description: 'High-rise residential building integrating vertical gardens and renewable energy systems for urban sustainability.',
      tags: ['Green', 'Vertical', 'Urban'],
      status: 'Draft'
    },
    {
      id: 3,
      title: 'Smart Campus Master Plan',
      category: 'Urban Planning',
      author: 'Marcus Rodriguez',
      date: '2024-01-10',
      likes: 32,
      image: '/assets/UrbanPlanning.jpeg',
      description: 'Comprehensive campus design integrating smart technology, sustainable infrastructure, and student-centered spaces.',
      tags: ['Smart', 'Campus', 'Technology'],
      status: 'Published'
    },
    {
      id: 4,
      title: 'Parametric Facade System',
      category: 'Engineering',
      author: 'Elena Kowalski',
      date: '2024-01-08',
      likes: 15,
      image: '/assets/AI & Machine Learning.jpeg',
      description: 'AI-driven parametric facade design optimizing daylight, ventilation, and energy performance through computational design.',
      tags: ['Parametric', 'AI', 'Facade'],
      status: 'Review'
    },
    {
      id: 5,
      title: 'Seismic Retrofit Study',
      category: 'Engineering',
      author: 'David Kim',
      date: '2024-01-05',
      likes: 28,
      image: '/assets/Structural analysis.jpeg',
      description: 'Advanced structural analysis and seismic retrofit design for historic building preservation and safety enhancement.',
      tags: ['Seismic', 'Retrofit', 'Historic'],
      status: 'Published'
    },
    {
      id: 6,
      title: 'Biomimetic Architecture',
      category: 'Research',
      author: 'Maria Santos',
      date: '2024-01-03',
      likes: 22,
      image: '/assets/NEW AGE BUILDING.png',
      description: 'Research project exploring nature-inspired design solutions for energy-efficient building systems and adaptive facades.',
      tags: ['Biomimetic', 'Research', 'Nature'],
      status: 'Published'
    },
    {
      id: 7,
      title: 'Modular Housing System',
      category: 'Residential',
      author: 'John Wilson',
      date: '2024-01-01',
      likes: 19,
      image: '/assets/GROUNDWORK IN PROGRESS.jpeg',
      description: 'Prefabricated modular housing solution for rapid deployment in disaster-affected areas and affordable housing.',
      tags: ['Modular', 'Affordable', 'Disaster'],
      status: 'Draft'
    },
    {
      id: 8,
      title: 'Heritage Conservation',
      category: 'Cultural',
      author: 'Ahmed Hassan',
      date: '2023-12-28',
      likes: 31,
      image: '/assets/Rapid Urbanization.jpeg',
      description: 'Comprehensive conservation and adaptive reuse strategy for historic urban districts facing rapid development pressure.',
      tags: ['Heritage', 'Conservation', 'Adaptive'],
      status: 'Published'
    }
  ];

  // Initialize projects on component mount
  useEffect(() => {
    setProjects(initialProjects);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Save liked projects to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('likedProjects', JSON.stringify(Array.from(likedProjects)));
  }, [likedProjects]);

  const handleLikeToggle = (projectId, e) => {
    e.stopPropagation(); // Prevent triggering the project click
    setLikedProjects(prev => {
      const newLiked = new Set(prev);
      if (newLiked.has(projectId)) {
        newLiked.delete(projectId);
      } else {
        newLiked.add(projectId);
      }
      return newLiked;
    });
  };

  const handleEditProject = (project, e) => {
    e.stopPropagation();
    setEditingProject(project);
    setShowEditModal(true);
  };

  const handleDeleteClick = (project, e) => {
    e.stopPropagation();
    setProjectToDelete(project);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = () => {
    if (projectToDelete) {
      setProjects(prev => prev.filter(p => p.id !== projectToDelete.id));
      // Remove from liked projects if it was liked
      setLikedProjects(prev => {
        const newLiked = new Set(prev);
        newLiked.delete(projectToDelete.id);
        return newLiked;
      });
      setShowDeleteConfirm(false);
      setProjectToDelete(null);
    }
  };

  const handleEditSubmit = (editedProject) => {
    setProjects(prev => prev.map(p => p.id === editedProject.id ? editedProject : p));
    setShowEditModal(false);
    setEditingProject(null);
  };

  // Close filter dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterRef.current && !filterRef.current.contains(event.target)) {
        setIsFilterOpen(false);
      }
    };

    if (isFilterOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isFilterOpen]);

  // Close modal on ESC key
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setShowProjectModal(false);
        setShowUploadModal(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  const categories = [
    { id: 'all', label: 'All Projects', count: projects.length },
    { id: 'Residential', label: 'Residential', count: projects.filter(p => p.category === 'Residential').length },
    { id: 'Commercial', label: 'Commercial', count: projects.filter(p => p.category === 'Commercial').length },
    { id: 'Urban Planning', label: 'Urban Planning', count: projects.filter(p => p.category === 'Urban Planning').length },
    { id: 'Cultural', label: 'Cultural', count: projects.filter(p => p.category === 'Cultural').length },
    { id: 'Infrastructure', label: 'Infrastructure', count: projects.filter(p => p.category === 'Infrastructure').length },
    { id: 'Hospitality', label: 'Hospitality', count: projects.filter(p => p.category === 'Hospitality').length }
  ];

  const filteredProjects = projects
    .filter(project => {
      const matchesCategory = activeFilter === 'all' || project.category === activeFilter;
      const matchesSearch = project.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           project.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           project.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
      return matchesCategory && matchesSearch;
    })
    .sort((a, b) => {
      // Liked projects first
      const aLiked = likedProjects.has(a.id);
      const bLiked = likedProjects.has(b.id);
      
      if (aLiked && !bLiked) return -1;
      if (!aLiked && bLiked) return 1;
      return 0;
    });

  const handleSearch = (searchTerm) => {
    setSearchTerm(searchTerm);
  };

  const handleProjectClick = (project) => {
    setSelectedProject(project);
    setShowProjectModal(true);
  };

  const handleUploadSubmit = (newProject) => {
    setProjects(prev => [newProject, ...prev]);
    setShowUploadModal(false);
  };

  const getStatusColor = (status) => {
    if (isDarkMode) {
      switch(status) {
        case 'Published': return 'bg-green-900/30 text-green-400 border border-green-700/50';
        case 'Draft': return 'bg-yellow-900/30 text-yellow-400 border border-yellow-700/50';
        case 'Review': return 'bg-blue-900/30 text-blue-400 border border-blue-700/50';
        default: return 'bg-gray-800 text-gray-300 border border-gray-700';
      }
    }
    switch(status) {
      case 'Published': return 'bg-green-100 text-green-800 border border-green-200';
      case 'Draft': return 'bg-yellow-100 text-yellow-800 border border-yellow-200';
      case 'Review': return 'bg-blue-100 text-blue-800 border border-blue-200';
      default: return 'bg-gray-100 text-gray-800 border border-gray-200';
    }
  };

  const handleNavigationClick = (item) => {
    if (item.isLogout) {
      navigate(item.path);
    } else {
      navigate(item.path);
    }
  };

  const toggleSidebar = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  const menuItems = [
    { label: 'HOME', path: '/app' },
    { label: 'STUDY', path: '/app/study' },
    { label: 'CHALLENGES', path: '/app/challenges' },
    { label: 'COMMUNITY', path: '/app/community' },
    { label: 'PIN UP', path: '/app/pinup' },
    { label: 'PROGRESS', path: '/app/progress' },
    { label: 'PROFILE', path: '/app/profile' },
    { label: 'LOG OUT', path: '/login', isLogout: true }
  ];

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-gray-900' : 'bg-gray-100'}`}>
      {/* Sidebar Navigation */}
      <div className="fixed top-0 left-0 h-screen z-40">
        <NavigationCarousel
          menuItems={menuItems}
          onItemClick={handleNavigationClick}
          isCollapsed={isSidebarCollapsed}
          onToggleSidebar={toggleSidebar}
        />
      </div>

      {/* Main Content - Adjusted for Sidebar */}
      <div className={`transition-all duration-300 ${isSidebarCollapsed ? 'ml-20' : 'ml-56'}`}>
        {/* Custom Header */}
        <DashboardHeader title="PIN UP" isSidebarCollapsed={isSidebarCollapsed} />

        {/* Search Bar Section */}
        <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-sm`}>
          <div className="max-w-7xl px-6 py-4" style={{ 
            marginLeft: isSidebarCollapsed ? '20px' : '20px',
            transition: 'margin-left 0.3s ease'
          }}>
            <SearchBar 
              placeholder="Search projects, tags, authors..." 
              onSearch={handleSearch}
            />
          </div>
        </div>

        <div className="max-w-7xl px-6 py-8" style={{ 
          marginLeft: isSidebarCollapsed ? '20px' : '20px',
          transition: 'margin-left 0.3s ease'
        }}>
        {/* Action Bar */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8">
          <div className={`text-sm font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-600'} mb-4 md:mb-0`}>
            {filteredProjects.length} {filteredProjects.length === 1 ? 'project' : 'projects'}
          </div>
          <div className="flex items-center gap-4">
            {/* Category Filter */}
            <div className="relative" ref={filterRef}>
              <button
                onClick={() => setIsFilterOpen(!isFilterOpen)}
                className={`flex items-center gap-2 ${
                  isDarkMode 
                    ? 'bg-gray-700 border-gray-600 text-gray-200 hover:bg-gray-600' 
                    : 'bg-gray-50 border-gray-300 text-gray-700 hover:bg-gray-100'
                } border rounded-lg px-4 py-2 text-sm font-medium transition-colors`}
              >
                <Filter size={16} className={isDarkMode ? 'text-gray-300' : 'text-gray-700'} />
                {categories.find(cat => cat.id === activeFilter)?.label || 'All Projects'}
                <ChevronDown size={16} className={`transition-transform ${isFilterOpen ? 'rotate-180' : ''} ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`} />
              </button>
              
              {isFilterOpen && (
                <div className={`absolute top-full right-0 mt-2 w-56 ${
                  isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                } border rounded-lg shadow-xl z-20 max-h-64 overflow-y-auto`}>
                  {categories.map((category) => (
                    <button
                      key={category.id}
                      onClick={() => {
                        setActiveFilter(category.id);
                        setIsFilterOpen(false);
                      }}
                      className={`w-full text-left px-4 py-2.5 text-sm font-medium transition-colors first:rounded-t-lg last:rounded-b-lg ${
                        activeFilter === category.id
                          ? 'bg-[#AC5757] text-white'
                          : isDarkMode
                            ? 'text-gray-300 hover:bg-gray-700'
                            : 'text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span>{category.label}</span>
                        <span className={`text-xs ${activeFilter === category.id ? 'text-white/80' : isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                          {category.count}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button 
              onClick={() => setShowUploadModal(true)}
              className="inline-flex items-center gap-2 bg-[#AC5757] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#8A4A4A] transition-colors"
            >
              <Plus size={20} />
              New Project
            </button>
          </div>
        </div>


        {/* Projects Horizontal Scrollable Row */}
        {filteredProjects.length === 0 ? (
          <div className={`text-center py-16 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            <p className="text-lg mb-2">No projects found</p>
            <p className="text-sm mb-6">Try adjusting your filters or create a new project</p>
            <button
              onClick={() => setShowUploadModal(true)}
              className="inline-flex items-center gap-2 bg-[#AC5757] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#8A4A4A] transition-colors"
            >
              <Plus size={20} />
              Create Your First Project
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto pb-4 -mx-4 sm:mx-0 px-4 sm:px-0 scrollbar-thin scrollbar-thumb-transparent scrollbar-track-transparent">
            <div className="flex gap-4 sm:gap-6 min-w-max">
              {filteredProjects.map((project) => (
                <div
                  key={project.id}
                  onClick={() => handleProjectClick(project)}
                  className={`${
                    isDarkMode 
                      ? 'bg-gray-800 border-gray-700 hover:border-gray-600 hover:shadow-xl' 
                      : 'bg-white border-gray-200 hover:border-gray-300 hover:shadow-lg'
                  } rounded-2xl border overflow-hidden transition-all duration-300 group cursor-pointer flex-shrink-0 w-72 sm:w-80`}
                >
                  <div className={`aspect-video overflow-hidden ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                    <img 
                      src={project.image} 
                      alt={project.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      onError={(e) => {
                        e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="225"%3E%3Crect fill="%23ddd" width="400" height="225"/%3E%3Ctext fill="%23999" font-family="sans-serif" font-size="18" dy="10.5" font-weight="bold" x="50%25" y="50%25" text-anchor="middle"%3EProject Image%3C/text%3E%3C/svg%3E';
                      }}
                    />
                  </div>
                  <div className="p-4 sm:p-6">
                    <div className="flex items-center justify-between mb-3">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${getStatusColor(project.status)}`}>
                        {project.status}
                      </span>
                      <div className="flex items-center gap-2">
                        <div className={`flex items-center gap-1.5 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          <Heart size={14} fill="currentColor" className="text-red-500" />
                          {project.likes}
                        </div>
                        <button
                          onClick={(e) => handleLikeToggle(project.id, e)}
                          className={`p-1.5 rounded-full transition-colors ${
                            likedProjects.has(project.id)
                              ? 'bg-red-100 hover:bg-red-200'
                              : isDarkMode 
                                ? 'hover:bg-gray-700' 
                                : 'hover:bg-gray-100'
                          }`}
                        >
                          <Heart 
                            size={16} 
                            fill={likedProjects.has(project.id) ? 'currentColor' : 'none'}
                            className={likedProjects.has(project.id) ? 'text-red-500' : (isDarkMode ? 'text-gray-400' : 'text-gray-500')}
                          />
                        </button>
                        <div className="relative group">
                          <button
                            onClick={(e) => e.stopPropagation()}
                            className={`p-1.5 rounded-full transition-colors ${
                              isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                            }`}
                          >
                            <MoreVertical size={16} className={isDarkMode ? 'text-gray-400' : 'text-gray-500'} />
                          </button>
                          <div className={`absolute right-0 top-8 w-40 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10`}>
                            <button
                              onClick={(e) => handleEditProject(project, e)}
                              className={`w-full px-4 py-2 flex items-center gap-2 hover:${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'} transition-colors ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}
                            >
                              <Edit size={14} />
                              <span className="text-sm">Edit</span>
                            </button>
                            <button
                              onClick={(e) => handleDeleteClick(project, e)}
                              className={`w-full px-4 py-2 flex items-center gap-2 hover:${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'} transition-colors ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}
                            >
                              <Trash2 size={14} />
                              <span className="text-sm">Delete</span>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                    <h3 className={`font-bold text-lg sm:text-xl mb-2 group-hover:text-[#AC5757] transition-colors ${
                      isDarkMode ? 'text-white' : 'text-gray-900'
                    }`}>
                      {project.title}
                    </h3>
                    <p className={`text-sm mb-4 line-clamp-2 ${
                      isDarkMode ? 'text-gray-400' : 'text-gray-600'
                    }`}>
                      {project.description}
                    </p>
                    <div className={`flex items-center justify-between text-xs mb-4 ${
                      isDarkMode ? 'text-gray-500' : 'text-gray-500'
                    }`}>
                      <div className="flex items-center gap-1.5">
                        <User size={12} className={isDarkMode ? 'text-gray-400' : 'text-gray-500'} />
                        <span className="truncate max-w-[100px]">{project.author}</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <Calendar size={12} className={isDarkMode ? 'text-gray-400' : 'text-gray-500'} />
                        {new Date(project.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {project.tags.slice(0, 2).map((tag, idx) => (
                        <span key={idx} className={`px-2 py-1 rounded text-xs ${
                          isDarkMode 
                            ? 'bg-gray-700 text-gray-300' 
                            : 'bg-gray-100 text-gray-600'
                        }`}>
                          {tag}
                        </span>
                      ))}
                      {project.tags.length > 2 && (
                        <span className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                          +{project.tags.length - 2}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Course Projects Section */}
        <div className="mt-12 sm:mt-16">
          <h2 className={`text-2xl sm:text-3xl font-bold mb-4 sm:mb-6 ${
            isDarkMode ? 'text-white' : 'text-gray-900'
          }`}>
            Course Projects
          </h2>
          
          {/* Course Icons Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {/* Spatial Reasoning Project */}
            <div 
              onClick={() => navigate('/app/course-projects')}
              className={`${
                isDarkMode 
                  ? 'bg-gray-800 border-gray-700 hover:border-gray-600 hover:shadow-xl' 
                  : 'bg-white border-gray-200 hover:border-gray-300 hover:shadow-lg'
              } rounded-xl border p-4 sm:p-6 transition-all duration-300 cursor-pointer group`}
            >
              <div className={`aspect-square ${
                isDarkMode 
                  ? 'bg-gradient-to-br from-[#AC5757]/20 to-[#AC5757]/10' 
                  : 'bg-gradient-to-br from-[#AC5757]/10 to-[#AC5757]/5'
              } rounded-lg flex items-center justify-center mb-4 group-hover:from-[#AC5757]/30 group-hover:to-[#AC5757]/20 transition-colors`}>
                <div className="text-center">
                  <div className="w-16 h-16 sm:w-20 sm:h-20 bg-[#AC5757] rounded-lg flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform">
                    <span className="text-white font-bold text-xl sm:text-2xl">SR</span>
                  </div>
                  <div className={`text-sm font-medium ${
                    isDarkMode ? 'text-gray-400' : 'text-gray-500'
                  }`}>3/12 weeks</div>
                </div>
              </div>
              <h3 className={`font-bold text-base sm:text-lg text-center mb-2 group-hover:text-[#AC5757] transition-colors ${
                isDarkMode ? 'text-white' : 'text-gray-900'
              }`}>
                INTRO TO SPATIAL REASONING
              </h3>
              <p className={`text-xs sm:text-sm text-center mb-3 ${
                isDarkMode ? 'text-gray-400' : 'text-gray-600'
              }`}>
                Design a multi-level parking structure with optimal circulation patterns
              </p>
              <div className="flex justify-center">
                <span className="bg-[#AC5757]/10 text-[#AC5757] px-3 py-1 rounded-full text-xs font-semibold border border-[#AC5757]/20">
                  In Progress
                </span>
              </div>
            </div>

            {/* BIM Project */}
            <div 
              onClick={() => navigate('/app/course-projects')}
              className={`${
                isDarkMode 
                  ? 'bg-gray-800 border-gray-700 hover:border-gray-600 hover:shadow-xl' 
                  : 'bg-white border-gray-200 hover:border-gray-300 hover:shadow-lg'
              } rounded-xl border p-4 sm:p-6 transition-all duration-300 cursor-pointer group`}
            >
              <div className={`aspect-square ${
                isDarkMode 
                  ? 'bg-gradient-to-br from-[#AC5757]/20 to-[#AC5757]/10' 
                  : 'bg-gradient-to-br from-[#AC5757]/10 to-[#AC5757]/5'
              } rounded-lg flex items-center justify-center mb-4 group-hover:from-[#AC5757]/30 group-hover:to-[#AC5757]/20 transition-colors`}>
                <div className="text-center">
                  <div className="w-16 h-16 sm:w-20 sm:h-20 bg-[#AC5757] rounded-lg flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform">
                    <span className="text-white font-bold text-xl sm:text-2xl">BIM</span>
                  </div>
                  <div className={`text-sm font-medium ${
                    isDarkMode ? 'text-gray-400' : 'text-gray-500'
                  }`}>7/14 weeks</div>
                </div>
              </div>
              <h3 className={`font-bold text-base sm:text-lg text-center mb-2 group-hover:text-[#AC5757] transition-colors ${
                isDarkMode ? 'text-white' : 'text-gray-900'
              }`}>
                BIM APPLI.: GIS MAPPING
              </h3>
              <p className={`text-xs sm:text-sm text-center mb-3 ${
                isDarkMode ? 'text-gray-400' : 'text-gray-600'
              }`}>
                Create a 3D city model integrating GIS data with building information
              </p>
              <div className="flex justify-center">
                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                  isDarkMode 
                    ? 'bg-green-900/30 text-green-400 border border-green-700/50' 
                    : 'bg-green-100 text-green-800 border border-green-200'
                }`}>
                  Completed
                </span>
              </div>
            </div>

            {/* Sustainability Project */}
            <div 
              onClick={() => navigate('/app/course-projects')}
              className={`${
                isDarkMode 
                  ? 'bg-gray-800 border-gray-700 hover:border-gray-600 hover:shadow-xl' 
                  : 'bg-white border-gray-200 hover:border-gray-300 hover:shadow-lg'
              } rounded-xl border p-4 sm:p-6 transition-all duration-300 cursor-pointer group`}
            >
              <div className={`aspect-square ${
                isDarkMode 
                  ? 'bg-gradient-to-br from-[#AC5757]/20 to-[#AC5757]/10' 
                  : 'bg-gradient-to-br from-[#AC5757]/10 to-[#AC5757]/5'
              } rounded-lg flex items-center justify-center mb-4 group-hover:from-[#AC5757]/30 group-hover:to-[#AC5757]/20 transition-colors`}>
                <div className="text-center">
                  <div className="w-16 h-16 sm:w-20 sm:h-20 bg-[#AC5757] rounded-lg flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform">
                    <span className="text-white font-bold text-xl sm:text-2xl">SD</span>
                  </div>
                  <div className={`text-sm font-medium ${
                    isDarkMode ? 'text-gray-400' : 'text-gray-500'
                  }`}>3/13 weeks</div>
                </div>
              </div>
              <h3 className={`font-bold text-base sm:text-lg text-center mb-2 group-hover:text-[#AC5757] transition-colors ${
                isDarkMode ? 'text-white' : 'text-gray-900'
              }`}>
                INTRO TO SUSTAINABILITY & DESIGN
              </h3>
              <p className={`text-xs sm:text-sm text-center mb-3 ${
                isDarkMode ? 'text-gray-400' : 'text-gray-600'
              }`}>
                Design a net-zero energy residential complex with renewable systems
              </p>
              <div className="flex justify-center">
                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                  isDarkMode 
                    ? 'bg-yellow-900/30 text-yellow-400 border border-yellow-700/50' 
                    : 'bg-yellow-100 text-yellow-800 border border-yellow-200'
                }`}>
                  Draft
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Load More Button */}
        {filteredProjects.length > 0 && (
          <div className="text-center mt-12">
            <button 
              onClick={() => alert('Loading more projects...\n\nThis would fetch additional projects from the database.')}
              className={`${
                isDarkMode 
                  ? 'bg-gray-800 border-gray-700 text-gray-200 hover:bg-gray-700' 
                  : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
              } border px-8 py-3 rounded-lg font-semibold transition-colors`}
            >
              Load More Projects
            </button>
          </div>
        )}
        </div>

      {/* Project Detail Modal */}
      {showProjectModal && selectedProject && (
        <div 
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowProjectModal(false);
            }
          }}
        >
          <div className={`${
            isDarkMode ? 'bg-gray-800' : 'bg-white'
          } rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden shadow-2xl animate-in slide-in-from-bottom-4 duration-300`}>
            {/* Modal Header */}
            <div className={`sticky top-0 ${
              isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
            } border-b p-4 sm:p-6 z-10`}>
              <div className="flex justify-between items-start gap-4">
                <h2 className={`text-xl sm:text-2xl font-bold ${
                  isDarkMode ? 'text-white' : 'text-gray-900'
                } flex-1`}>
                  {selectedProject.title}
                </h2>
                <button 
                  onClick={() => setShowProjectModal(false)}
                  className={`${
                    isDarkMode 
                      ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700' 
                      : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
                  } p-2 rounded-full transition-colors flex-shrink-0`}
                >
                  <X size={20} />
                </button>
              </div>
            </div>
            
            {/* Modal Content */}
            <div className="overflow-y-auto max-h-[calc(90vh-80px)]">
              <div className="p-4 sm:p-6">
                <div className={`aspect-video mb-6 rounded-lg overflow-hidden ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <img 
                    src={selectedProject.image} 
                    alt={selectedProject.title}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="800" height="450"%3E%3Crect fill="%23ddd" width="800" height="450"/%3E%3Ctext fill="%23999" font-family="sans-serif" font-size="24" dy="10.5" font-weight="bold" x="50%25" y="50%25" text-anchor="middle"%3EProject Image%3C/text%3E%3C/svg%3E';
                    }}
                  />
                </div>
                
                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(selectedProject.status)}`}>
                    {selectedProject.status}
                  </span>
                  <div className={`flex items-center gap-2 text-sm ${
                    isDarkMode ? 'text-gray-400' : 'text-gray-500'
                  }`}>
                    <User size={16} className={isDarkMode ? 'text-gray-400' : 'text-gray-500'} />
                    {selectedProject.author}
                  </div>
                  <div className={`flex items-center gap-2 text-sm ${
                    isDarkMode ? 'text-gray-400' : 'text-gray-500'
                  }`}>
                    <Calendar size={16} className={isDarkMode ? 'text-gray-400' : 'text-gray-500'} />
                    {new Date(selectedProject.date).toLocaleDateString('en-US', { 
                      month: 'long', 
                      day: 'numeric', 
                      year: 'numeric' 
                    })}
                  </div>
                  <div className={`flex items-center gap-2 text-sm ${
                    isDarkMode ? 'text-gray-400' : 'text-gray-500'
                  }`}>
                    <Heart size={16} fill="currentColor" className="text-red-500" />
                    {selectedProject.likes} {selectedProject.likes === 1 ? 'like' : 'likes'}
                  </div>
                </div>
                
                <p className={`text-base sm:text-lg mb-6 leading-relaxed ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  {selectedProject.description}
                </p>
                
                <div className="flex items-center gap-2 mb-6 flex-wrap">
                  {selectedProject.tags.map((tag, idx) => (
                    <span key={idx} className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
                      isDarkMode 
                        ? 'bg-gray-700 text-gray-300' 
                        : 'bg-gray-100 text-gray-700'
                    }`}>
                      {tag}
                    </span>
                  ))}
                </div>
                
                <div className={`flex flex-col sm:flex-row gap-3 pt-4 border-t ${
                  isDarkMode ? 'border-gray-700' : 'border-gray-200'
                }`}>
                  <button 
                    onClick={() => {
                      setShowProjectModal(false);
                      alert(`Opening ${selectedProject.title} in full view...\n\nThis would launch the project in a detailed viewer with additional images, 3D models, and project documentation.`);
                    }}
                    className="flex-1 bg-[#AC5757] text-white py-3 px-6 rounded-lg font-semibold hover:bg-[#8A4A4A] transition-colors shadow-sm"
                  >
                    View Full Project
                  </button>
                  <button 
                    onClick={() => {
                      if (selectedProject) {
                        handleLikeToggle(selectedProject.id, { stopPropagation: () => {} });
                      }
                    }}
                    className={`px-6 py-3 border rounded-lg transition-colors flex items-center justify-center gap-2 ${
                      likedProjects.has(selectedProject?.id)
                        ? 'bg-red-500 text-white border-red-500 hover:bg-red-600'
                        : isDarkMode 
                          ? 'border-gray-700 text-gray-300 hover:bg-gray-700' 
                          : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <Heart 
                      size={16} 
                      fill={likedProjects.has(selectedProject?.id) ? 'currentColor' : 'none'}
                    />
                    {likedProjects.has(selectedProject?.id) ? 'Liked' : 'Like'}
                  </button>
                  <button 
                    onClick={() => alert('Project shared!')}
                    className={`px-6 py-3 border rounded-lg transition-colors flex items-center justify-center gap-2 ${
                      isDarkMode 
                        ? 'border-gray-700 text-gray-300 hover:bg-gray-700' 
                        : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <Share2 size={16} />
                    Share
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

        {/* Upload Modal */}
        <ProjectUploadModal
          isOpen={showUploadModal}
          onClose={() => setShowUploadModal(false)}
          onSubmit={handleUploadSubmit}
        />

        {/* Edit Modal */}
        {showEditModal && editingProject && (
          <ProjectUploadModal
            isOpen={showEditModal}
            onClose={() => {
              setShowEditModal(false);
              setEditingProject(null);
            }}
            onSubmit={handleEditSubmit}
            projectToEdit={editingProject}
          />
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && projectToDelete && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowDeleteConfirm(false)}>
            <div 
              className={`max-w-md w-full ${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-2xl shadow-2xl p-6`}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center gap-4 mb-6">
                <div className={`p-3 rounded-full ${isDarkMode ? 'bg-red-900/30' : 'bg-red-100'}`}>
                  <Trash2 size={24} className="text-red-600" />
                </div>
                <div>
                  <h3 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    Delete Project
                  </h3>
                  <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    This action cannot be undone
                  </p>
                </div>
              </div>
              
              <p className={`mb-6 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Are you sure you want to delete <span className="font-semibold">"{projectToDelete.title}"</span>? This will permanently remove the project from your collection.
              </p>
              
              <div className="flex gap-3">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className={`flex-1 px-4 py-3 rounded-lg font-medium transition-colors ${
                    isDarkMode
                      ? 'bg-gray-700 text-gray-200 hover:bg-gray-600'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDelete}
                  className="flex-1 px-4 py-3 rounded-lg font-medium bg-red-600 text-white hover:bg-red-700 transition-colors"
                >
                  Delete Project
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Pinup;
